import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ViewContainerRef,
} from '@angular/core';
import 'reflect-metadata';
import { PreviousRouteService } from '../../shared/previous-route.service';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, takeUntil, take, map, startWith, debounceTime, mergeMap } from 'rxjs/operators';
import { Subject, Observable, fromEvent } from 'rxjs';
import { StorageService, LocalStorageEnum } from '../../shared/storage.service';
import { UserProfileModel } from '../../auth/models/user-profile.model';
import { get, isNil } from 'lodash';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { AuthState } from '../../auth/state/auth/auth.state';
import { AuthService } from '../../auth/auth.service';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { PendoService } from 'src/app/shared/pendo.service';
import { MatDialog } from '@angular/material/dialog';
import { GroupRestrictionDialogComponent } from 'src/app/dialogs/components/group-restriction-dialog/group-restriction-dialog.component';
import { DialogIds } from 'src/app/dialogs/models/dialog-ids';
import { LinkedinInsightsService } from 'src/app/shared/linkedin-insights.service';
import { FacebookPixelService } from 'src/app/shared/facebook-pixel.service';
import { WidgetAPIHandler } from 'src/app/widget-api/api-handler';
import { EngineService } from 'src/app/engine/engine.service';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { DeleteStoredData } from 'src/app/auth/state/auth/auth.actions';
import { environment } from 'src/environments/environment';
import { HumanWindowAPI } from 'src/app/widget-api/human-window-api';
import { ZendeskService } from 'src/app/shared/features/zendesk/zendesk.service';
import { LocaleModel } from 'src/app/shared/models/locale.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CompatibilityService } from 'src/app/shared/compatibility.service';
import { ViewService } from 'src/app/view/view.service';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'human-studio-next';

  @Select(AuthState.getUserProfile)
  userProfile$: Observable<UserProfileModel>;

  @Select(AuthState.getUserLocale)
  userLocale$: Observable<LocaleModel>;

  private ngUnsubscribe = new Subject();

  constructor(
    previousRouteService: PreviousRouteService,
    private ga: GoogleAnalyticsService,
    private authService: AuthService,
    private storageService: StorageService,
    private compatibilityService: CompatibilityService,
    private dialog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public viewContainerRef: ViewContainerRef,
    private linkedinInsights: LinkedinInsightsService,
    private facebookPixel: FacebookPixelService,
    private pendoService: PendoService,
    private widgetAPIHandler: WidgetAPIHandler,
    private engineService: EngineService,
    private store: Store,
    private actions: Actions,
    private zendeskService: ZendeskService,
    private viewService: ViewService,
    private snackBar: MatSnackBar,
    private titleService: Title,
  ) {
    previousRouteService.loadRouting();
    // Init page title
    this.initPageTitle();
  }

  ngOnInit() {
    // Init page tracking
    this.initPageAnalytics();

    // init API: native only
    if (environment.native) {
      // check API usage
      this.initNativeAPIHooks();
    }

    // Check profile access
    this.userProfile$
      .pipe(
        filter((profile) => profile != null),
        take(1),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((profile) => {
        this.checkProfileAccess(profile);
      });

    // Init help center
    this.initZendesk();

    // Init Pendo Analytics
    this.initPendo();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initPageTitle() {
    // Capture app page title
    const appTitle = this.titleService.getTitle();

    // Set title based on navigation changes. The title is pulled from the data attribute in *-routing.module.ts files.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let pageTitle = '';
          let route = this.activatedRoute;

          while (route.firstChild) {
            const title = route.firstChild.snapshot.data.title;
            if (!isNil(title)) {
              pageTitle = title;
            }
            route = route.firstChild;
          }
          return pageTitle;
        }),
        map((pageTitle) => {
          if (pageTitle.length) {
            return `${pageTitle} | BioDigital Human Platform`;
          }
          return appTitle;
        }),
      )
      .subscribe((title) => {
        this.titleService.setTitle(title);
      });
  }

  initPageAnalytics() {
    this.linkedinInsights.initialize();
    this.facebookPixel.initialize();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        this.ga.pageView(url);
      });
  }

  initZendesk() {
    // global settings (position, etc...)
    this.zendeskService.updateFromProfile();

    // set help center suggestions (web vs mobile)
    fromEvent(window, 'resize')
      .pipe(
        startWith(this.compatibilityService.isHoverSupported()),
        debounceTime(100),
        map(() => this.compatibilityService.isHoverSupported()),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((isHoverSupported) => {
        this.zendeskService.setHelpCenterSuggestions({
          labels: [isHoverSupported ? 'webhelp' : 'mobilehelp'],
        });
      });

    // set identity
    this.userProfile$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((profile) => {
      this.zendeskService.identify(profile);
      this.zendeskService.updateFromProfile(profile);
    });

    // set locale
    this.userLocale$
      .pipe(
        filter((userLocale) => !isNil(userLocale)),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((userLocale) => {
        const locale = userLocale.lang;
        // config zendesk
        this.zendeskService.setLocale(locale);
      });
  }

  initPendo() {
    this.pendoService.appendScript();
    this.pendoService.initialize({ visitor: {}, account: {} });

    this.userProfile$
      .pipe(
        filter((profile) => profile != null),
        take(1),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((profile) => {
        this.setPendoVisitorData(profile);
      });
  }

  setPendoVisitorData(userProfile: UserProfileModel) {
    this.pendoService.identify({
      visitor: this.pendoService.createVisitorData(userProfile),
      account: this.pendoService.createAccountData(userProfile.organizations[0]),
    });
  }

  checkProfileAccess(profile: UserProfileModel) {
    const profileRestrictions = this.authService.getProfileRestrictions(profile);
    const restrictionSettings = this.storageService.getLocalJSON(LocalStorageEnum.GroupRestriction);
    const showDialog = get(restrictionSettings, 'dontShowDialog') !== true;

    if (showDialog && (profileRestrictions.email || profileRestrictions.ip)) {
      setTimeout(() => {
        const options = {
          id: DialogIds.GroupRestrictionDialog,
          width: '480px',
          height: 'auto',
          data: {
            ...profileRestrictions,
          },
        };

        this.dialog.open(GroupRestrictionDialogComponent, options);
      });
    }
  }

  initNativeAPIHooks() {
    // inject Human API via Window
    (window as any).HumanWindowAPI = HumanWindowAPI;
    HumanWindowAPI.setStore(this.store);
    HumanWindowAPI.setSnackBar(this.snackBar);
    HumanWindowAPI.setRouter(this.router);

    // init + start
    this.widgetAPIHandler.listen(false);
    this.engineService.fireEvent(EventsEnum.HumanStarted, null);

    this.actions.pipe(ofActionDispatched(DeleteStoredData)).subscribe(() => {
      this.engineService.fireEvent(EventsEnum.NativeDeleteData, null);
    });
  }
}
